import apiClient, { PaginatedResponse } from './client';

export interface User {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  role: string;
  created_at: string;
  updated_at: string;
}

export interface UserCreate {
  email: string;
  password: string;
  name?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  role?: string;
}

export interface UserUpdate {
  name?: string;
  password?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  role?: string;
}

export interface ListUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

export interface UserRole {
  name: string;
  description: string;
}

interface RolesResponse {
  roles: string[];
  descriptions: Record<string, string>;
}

export const usersApi = {
  getRoles: async (): Promise<UserRole[]> => {
    const response = await apiClient.get<RolesResponse>('/api/v1/users/roles');
    const { roles, descriptions } = response.data;
    return roles.map((name) => ({ name, description: descriptions[name] ?? '' }));
  },

  list: async (params: ListUsersParams = {}): Promise<PaginatedResponse<User>> => {
    const response = await apiClient.get<PaginatedResponse<User>>('/api/v1/users', { params });
    return response.data;
  },

  get: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/api/v1/users/${id}`);
    return response.data;
  },

  create: async (data: UserCreate): Promise<User> => {
    const response = await apiClient.post<User>('/api/v1/users', data);
    return response.data;
  },

  update: async (id: string, data: UserUpdate): Promise<User> => {
    const response = await apiClient.patch<User>(`/api/v1/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/users/${id}`);
  },

  // Current user
  getMe: async (): Promise<User> => {
    const response = await apiClient.get<User>('/api/v1/users/me');
    return response.data;
  },

  updateMe: async (data: Omit<UserUpdate, 'is_active' | 'is_superuser'>): Promise<User> => {
    const response = await apiClient.patch<User>('/api/v1/users/me', data);
    return response.data;
  },
};
